import { InformationCircleIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect } from "react";

interface ManualScoreEditorProps {
  autoScore: boolean;
  currentScore?: number;
  calculatedScore: number;
  scoreJustification?: string;
  commitmentType: "committed" | "aspirational";
  onScoreChange: (
    autoScore: boolean,
    score?: number,
    justification?: string,
  ) => Promise<void>;
  disabled?: boolean;
}

export const ManualScoreEditor: React.FC<ManualScoreEditorProps> = ({
  autoScore,
  currentScore,
  calculatedScore,
  scoreJustification,
  commitmentType,
  onScoreChange,
  disabled = false,
}) => {
  const [isAuto, setIsAuto] = useState(autoScore);
  const [manualScore, setManualScore] = useState(
    currentScore || calculatedScore,
  );
  const [justification, setJustification] = useState(scoreJustification || "");
  const [isSaving, setIsSaving] = useState(false);

  // Track if there are any unsaved changes
  const hasUnsavedChanges =
    isAuto !== autoScore ||
    (!isAuto && manualScore !== (currentScore || calculatedScore)) ||
    (!isAuto && justification !== (scoreJustification || ""));

  useEffect(() => {
    setIsAuto(autoScore);
    setManualScore(currentScore || calculatedScore);
    setJustification(scoreJustification || "");
  }, [autoScore, currentScore, calculatedScore, scoreJustification]);

  const handleScoringMethodChange = (auto: boolean) => {
    setIsAuto(auto);
    // When switching to manual, initialize with calculated score if no manual score exists
    if (!auto && !currentScore) {
      setManualScore(calculatedScore);
    }
  };

  const handleManualScoreChange = (value: number) => {
    setManualScore(value);
  };

  const handleJustificationChange = (text: string) => {
    setJustification(text);
  };

  const handleApplyChanges = async () => {
    if (!hasUnsavedChanges) return;

    setIsSaving(true);
    try {
      if (isAuto) {
        // When switching to automatic, pass undefined score to trigger recalculation
        // The backend should recalculate based on Key Results
        await onScoreChange(true, undefined, undefined);
      } else {
        await onScoreChange(false, manualScore, justification);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setIsAuto(autoScore);
    setManualScore(currentScore || calculatedScore);
    setJustification(scoreJustification || "");
  };

  const getScoreColor = (score: number) => {
    if (commitmentType === "committed") {
      if (score >= 100) return "text-green-600";
      if (score >= 70) return "text-yellow-600";
      return "text-red-600";
    } else {
      if (score >= 70) return "text-green-600";
      if (score >= 40) return "text-yellow-600";
      return "text-red-600";
    }
  };

  const getTargetZone = () => {
    if (commitmentType === "committed") {
      return { min: 100, max: 100, label: "100% Required" };
    } else {
      return { min: 60, max: 70, label: "60-70% Target" };
    }
  };

  const targetZone = getTargetZone();

  // Display score shows what WILL be saved based on current selection
  const displayScore = isAuto ? calculatedScore : manualScore;

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-start justify-between">
        <h4 className="text-sm font-medium text-gray-900">
          Scoring Method
          <button
            type="button"
            className="ml-1 inline-flex items-center text-gray-400 hover:text-gray-600"
            title="Scoring methodology based on Google OKR framework"
          >
            <InformationCircleIcon className="h-4 w-4" />
          </button>
        </h4>
        <div className="text-sm text-gray-500">
          {commitmentType === "committed"
            ? "Committed Goal"
            : "Aspirational Goal"}
        </div>
      </div>

      {/* Scoring Method Toggle */}
      <div className="space-y-3">
        <label className="flex items-start cursor-pointer">
          <input
            type="radio"
            checked={isAuto}
            onChange={() => handleScoringMethodChange(true)}
            disabled={disabled}
            className="mt-1 mr-3"
          />
          <div className="flex-1">
            <span className="font-medium">Automatic</span>
            <p className="text-xs text-gray-500">
              Calculate from Key Results progress
            </p>
            <div className="mt-2 p-2 bg-white rounded border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Calculated Score:</span>
                <span
                  className={`text-lg font-semibold ${getScoreColor(calculatedScore)}`}
                >
                  {calculatedScore.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </label>

        <label className="flex items-start cursor-pointer">
          <input
            type="radio"
            checked={!isAuto}
            onChange={() => handleScoringMethodChange(false)}
            disabled={disabled}
            className="mt-1 mr-3"
          />
          <div className="flex-1">
            <span className="font-medium">Manual Override</span>
            <p className="text-xs text-gray-500">
              Set custom score with justification
            </p>

            {!isAuto && (
              <div className="mt-3 space-y-3">
                {/* Score Slider */}
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-600">
                      Manual Score:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={manualScore}
                      onChange={(e) =>
                        handleManualScoreChange(Number(e.target.value))
                      }
                      disabled={disabled}
                      className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
                    />
                    <span
                      className={`text-lg font-semibold ${getScoreColor(manualScore)}`}
                    >
                      %
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={manualScore}
                      onChange={(e) =>
                        handleManualScoreChange(Number(e.target.value))
                      }
                      disabled={disabled}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />

                    {/* Target Zone Indicator */}
                    <div className="mt-2 relative h-2 bg-gray-100 rounded mb-6">
                      <div
                        className="absolute h-2 bg-blue-200 rounded"
                        style={{
                          left: `${targetZone.min}%`,
                          width: `${targetZone.max - targetZone.min || 1}%`,
                        }}
                      />
                      <div
                        className="absolute top-3 text-xs text-gray-500"
                        style={{
                          left:
                            commitmentType === "committed"
                              ? "auto"
                              : `${targetZone.min}%`,
                          right: commitmentType === "committed" ? "0" : "auto",
                        }}
                      >
                        {targetZone.label}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Justification */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Justification{" "}
                    <span className="text-gray-400">(recommended)</span>
                  </label>
                  <textarea
                    value={justification}
                    onChange={(e) => handleJustificationChange(e.target.value)}
                    disabled={disabled}
                    placeholder="Explain why manual scoring is needed (e.g., qualitative factors, external constraints, strategic adjustments)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                    rows={3}
                  />
                </div>

                {/* Comparison with Auto Score */}
                {!isAuto && calculatedScore !== manualScore && (
                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-yellow-800">
                        Difference from auto-calculated:
                      </span>
                      <span className="font-medium text-yellow-900">
                        {manualScore > calculatedScore ? "+" : ""}
                        {(manualScore - calculatedScore).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Current Score Display */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-600">Current Score:</span>
            <span
              className={`ml-2 text-xl font-bold ${getScoreColor(displayScore)}`}
            >
              {displayScore.toFixed(0)}%
            </span>
            {!autoScore && (
              <span className="ml-2 text-xs text-gray-500">(Manual)</span>
            )}
          </div>

          <div className="text-sm">
            {commitmentType === "committed" ? (
              displayScore >= 100 ? (
                <span className="text-green-600 font-medium">✓ Complete</span>
              ) : (
                <span className="text-yellow-600">Incomplete</span>
              )
            ) : displayScore >= 70 ? (
              <span className="text-green-600 font-medium">✓ Excellent</span>
            ) : displayScore >= 60 ? (
              <span className="text-green-600">On Target</span>
            ) : displayScore >= 40 ? (
              <span className="text-yellow-600">Making Progress</span>
            ) : (
              <span className="text-red-600">Early Stage</span>
            )}
          </div>
        </div>
      </div>

      {/* Apply/Cancel Buttons - Show for ANY changes */}
      {hasUnsavedChanges && (
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApplyChanges}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? "Applying..." : "Apply Changes"}
          </button>
        </div>
      )}
    </div>
  );
};
